import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Send, CheckCircle2, Star } from "lucide-react";
import SEO from "../components/SEO";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const QUESTIONS = [
  { key: "overall", label: "Overall satisfaction with our service" },
  { key: "caregiver", label: "Quality and professionalism of your caregiver" },
  { key: "communication", label: "Communication from our office" },
  { key: "timeliness", label: "Timeliness and reliability" },
  { key: "recommend", label: "How likely are you to recommend us?" },
];

function StarRow({ value, onChange, testid }) {
  return (
    <div className="flex items-center gap-1.5" data-testid={testid}>
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          onClick={() => onChange(n)}
          data-testid={`${testid}-${n}`}
          aria-label={`${n} star${n > 1 ? "s" : ""}`}
          className="transition-transform hover:scale-110"
        >
          <Star
            className={`w-7 h-7 ${
              n <= value ? "text-brand-amber fill-brand-amber" : "text-gray-300"
            }`}
          />
        </button>
      ))}
    </div>
  );
}

export default function Survey() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    ratings: { overall: 0, caregiver: 0, communication: 0, timeliness: 0, recommend: 0 },
    did_well: "",
    improve: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const setRating = (key, val) => setForm((f) => ({ ...f, ratings: { ...f.ratings, [key]: val } }));

  const onSubmit = async (ev) => {
    ev.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/surveys`, form);
      setDone(true);
      toast.success("Thank you for your feedback!");
    } catch (err) {
      const detail = err?.response?.data?.detail || "Submission failed.";
      toast.error(typeof detail === "string" ? detail : "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <SEO
        title="Client Satisfaction Survey | 1st Home Care Services"
        description="Share your feedback about 1st Home Care Services. Your responses help us deliver better home care across Northern Virginia."
        canonical="https://1sthomecare.com/survey"
      />

      <section className="container-pad section-pad">
        <div className="max-w-3xl">
          <div className="text-xs font-bold uppercase tracking-widest text-brand-amber mb-3">
            Client Satisfaction Survey
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight text-brand-blue-dark text-balance">
            How are we doing?
          </h1>
          <p className="mt-6 text-lg text-gray-600 leading-relaxed">
            Your feedback helps us deliver better care. Please take a moment to
            rate your experience — your responses are reviewed by our nursing leadership.
          </p>
        </div>
      </section>

      <section className="container-pad pb-16 md:pb-24">
        {done ? (
          <div
            data-testid="survey-success"
            className="max-w-2xl bg-white rounded-2xl border border-gray-100 shadow-md p-10 text-center"
          >
            <div className="mx-auto w-14 h-14 rounded-full bg-emerald-50 flex items-center justify-center mb-4">
              <CheckCircle2 className="w-7 h-7 text-emerald-600" />
            </div>
            <h2 className="font-heading text-2xl font-semibold text-brand-blue-dark">
              Thank you for your feedback
            </h2>
            <p className="text-gray-600 mt-2">
              We've received your survey. Your voice helps us improve our care every day.
            </p>
          </div>
        ) : (
          <form
            onSubmit={onSubmit}
            data-testid="survey-form"
            className="max-w-3xl bg-white rounded-2xl border border-gray-100 shadow-md p-6 md:p-10"
          >
            <div className="grid gap-4 md:grid-cols-2 mb-8">
              <div>
                <Label htmlFor="sv-name">Your Name (optional)</Label>
                <Input id="sv-name" data-testid="sv-input-name" className="mt-1"
                  value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </div>
              <div>
                <Label htmlFor="sv-email">Your Email (optional)</Label>
                <Input id="sv-email" type="email" data-testid="sv-input-email" className="mt-1"
                  value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </div>
            </div>

            <h3 className="font-heading text-sm font-bold uppercase tracking-widest text-brand-blue mb-4">
              Rate your experience
            </h3>
            <div className="space-y-5">
              {QUESTIONS.map((q) => (
                <div
                  key={q.key}
                  data-testid={`survey-question-${q.key}`}
                  className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 p-4 bg-brand-gray/50 rounded-xl"
                >
                  <span className="text-sm font-medium text-gray-800">{q.label}</span>
                  <StarRow value={form.ratings[q.key]} onChange={(v) => setRating(q.key, v)} testid={`survey-rating-${q.key}`} />
                </div>
              ))}
            </div>

            <div className="grid gap-4 mt-8">
              <div>
                <Label htmlFor="sv-did-well">What did we do well?</Label>
                <Textarea id="sv-did-well" data-testid="sv-input-did-well" rows={3} className="mt-1"
                  value={form.did_well} onChange={(e) => setForm({ ...form, did_well: e.target.value })} />
              </div>
              <div>
                <Label htmlFor="sv-improve">What can we improve?</Label>
                <Textarea id="sv-improve" data-testid="sv-input-improve" rows={3} className="mt-1"
                  value={form.improve} onChange={(e) => setForm({ ...form, improve: e.target.value })} />
              </div>
            </div>

            <Button type="submit" data-testid="survey-submit" disabled={submitting}
              className="w-full mt-6 h-12 bg-brand-blue hover:bg-brand-blue-dark text-white font-semibold rounded-full">
              {submitting ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting…</>) : (<><Send className="w-4 h-4 mr-2" /> Submit Survey</>)}
            </Button>
          </form>
        )}
      </section>
    </>
  );
}
